import { useState } from 'react'
import { api, ApiRequestError } from '../api/client'
import type { ApplicantView } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { number, percent } from '../lib/format'
import { Avatar, EmptyState, ErrorState, PageHeader, TableSkeleton } from '../components/ui'
import './Applications.css'

/**
 * Screen 7f · Admin · Moderator müraciətləri.
 *
 * The first two applications are shown as full cards with their motivation
 * text, and the rest as a compact "GÖZLƏYƏN SIRA" list — the design's own
 * split, which keeps the reviewer reading rather than scrolling.
 */
export function Applications() {
  const { data, error, loading, reload } = useAsync(() => api.applications(), [])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  async function decide(id: string, accept: boolean) {
    setBusyId(id)
    setActionError(null)
    try {
      await api.decideApplication(id, accept)
      reload()
    } catch (cause) {
      setActionError(
        cause instanceof ApiRequestError ? cause.message : 'Qərar yadda saxlanmadı.',
      )
    } finally {
      setBusyId(null)
    }
  }

  const applications = data ?? []
  const featured = applications.slice(0, 2)
  const queued = applications.slice(2)

  return (
    <>
      <PageHeader
        title="Moderator müraciətləri"
        subtitle={
          loading
            ? 'Yüklənir…'
            : `${number(applications.length)} yeni müraciət · minimum şərt: 500 ulduz, 30 gün, 0 pozuntu`
        }
      />

      {actionError && (
        <p className="applications__error" role="alert">
          {actionError}
        </p>
      )}

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading ? (
        <TableSkeleton rows={4} />
      ) : applications.length === 0 ? (
        <EmptyState
          title="Gözləyən müraciət yoxdur"
          description="Yeni müraciət gələndə burada görünəcək."
        />
      ) : (
        <>
          {featured.map((applicant) => (
            <ApplicantCard
              key={applicant.id}
              applicant={applicant}
              busy={busyId === applicant.id}
              onDecide={decide}
            />
          ))}

          {queued.length > 0 && (
            <section className="stack">
              <div className="overline">Gözləyən sıra · {queued.length}</div>

              <div className="table-scroll">
                <div className="table applications__table">
                  <div className="table__head">
                    <span>İstifadəçi</span>
                    <span>Ulduz</span>
                    <span>Seriya</span>
                    <span>Dəqiqlik</span>
                    <span>Hesab</span>
                    <span>Qərar</span>
                  </div>

                  {queued.map((applicant) => (
                    <div key={applicant.id} className="table__row">
                      <span className="row applications__person">
                        <Avatar initials={applicant.user.initials} />
                        <span className="applications__names">
                          <span className="applications__name">
                            {applicant.user.displayName}
                          </span>
                          <span className="muted applications__handle">
                            @{applicant.user.username}
                          </span>
                        </span>
                      </span>

                      <span className="numeral">{number(applicant.stars)}</span>
                      <span className="numeral">{number(applicant.streak)}</span>
                      <span
                        className={`numeral${
                          (applicant.accuracyPct ?? 100) < 75 ? ' applications__low' : ''
                        }`}
                      >
                        {percent(applicant.accuracyPct)}
                      </span>
                      <span className="muted">{number(applicant.accountAgeDays)} gün</span>

                      <span className="row">
                        <button
                          type="button"
                          className="btn btn--green btn--sm"
                          disabled={busyId === applicant.id}
                          onClick={() => void decide(applicant.id, true)}
                        >
                          Qəbul
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busyId === applicant.id}
                          onClick={() => void decide(applicant.id, false)}
                        >
                          Rədd
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </>
  )
}

function ApplicantCard({
  applicant,
  busy,
  onDecide,
}: {
  applicant: ApplicantView
  busy: boolean
  onDecide: (id: string, accept: boolean) => void
}) {
  return (
    <article className="card applicant">
      <div className="applicant__head">
        <Avatar initials={applicant.user.initials} />
        <div className="applicant__identity">
          <div className="applicant__name">{applicant.user.displayName}</div>
          <div className="muted applicant__handle">
            @{applicant.user.username} · {number(applicant.accountAgeDays)} gündür
          </div>
        </div>

        {!applicant.meetsRequirements && (
          <span className="chip chip--medium">Şərtlər tam deyil</span>
        )}
      </div>

      <dl className="applicant__stats">
        <div>
          <dt className="overline">Ulduz</dt>
          <dd className="numeral">{number(applicant.stars)}</dd>
        </div>
        <div>
          <dt className="overline">Seriya</dt>
          <dd className="numeral">{number(applicant.streak)} gün</dd>
        </div>
        <div>
          <dt className="overline">Dəqiqlik</dt>
          <dd className={`numeral${(applicant.accuracyPct ?? 100) < 75 ? ' applications__low' : ''}`}>
            {percent(applicant.accuracyPct)}
          </dd>
        </div>
      </dl>

      <blockquote className="applicant__motivation">“{applicant.motivation}”</blockquote>

      <div className="row">
        <button
          type="button"
          className="btn btn--green"
          disabled={busy}
          onClick={() => onDecide(applicant.id, true)}
        >
          Qəbul et
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() => onDecide(applicant.id, false)}
        >
          Rədd et
        </button>
      </div>
    </article>
  )
}
